import React, { useEffect, useState } from "react";
import axios from "axios";
import { FileText } from "lucide-react";
import { FiDownload } from "react-icons/fi";
import Select from 'react-select';
import { Base_Url, fetchUser, UploadCsv } from "../../../../../config/config";
import toast from "react-hot-toast";

const PricingTemplateUpload = () => {
  const [zoneFile, setZoneFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  const cus_id = localStorage.getItem("cus_id");
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [selectedCourier, setSelectedCourier] = useState("");
  const [selectedCftType, setSelectedCftType] = useState('');

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const courierPartners = [
    { id: "delhivery", name: "Delhivery" },
    { id: "movin", name: "Movin" },
    { id: "gati", name: "Gati" },
  ];

  const fetchRetailers = async () => {
    const url = Base_Url + fetchUser;
    setLoading(true);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          token: token,
          cus_id: cus_id,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.status === "success") {
        const transformedData = data.data.map((user) => {
          const capitalizedName =
            user.name.charAt(0).toUpperCase() + user.name.slice(1);
          return {
            value: user.id,
            label: `${capitalizedName} (${user.id})`,
          };
        });
        setRetailers(transformedData);
      } else {
        setError(data.message || "Failed to fetch retailers");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRetailers();
  }, []);

  const handleUpload = async () => {
    setSuccessMessage("");
    setError("");

    if (!selectedRetailer || !selectedCourier) {
      toast.error("Please select Retailer and Courier Partner");
      return;
    }

    if (!zoneFile) {
      toast.error("Please upload Zone Pricing CSV");
      return;
    }

    const formData = new FormData();
    formData.append("retailer_id", selectedRetailer.value);
    formData.append("type", selectedCftType);
    formData.append("courier_partner", selectedCourier);
    formData.append("zone_pricing", zoneFile);

    try {
      setLoading(true);

      const res = await axios.post(
        Base_Url + UploadCsv,
        formData,
        {
          headers: {
            token: token,
            cus_id: cus_id,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.status === "success") {
        toast.success(res.data.message || "Pricing uploaded successfully ✅");
        setSuccessMessage(res.data.message || "Pricing uploaded successfully ✅");
      } else {
        toast.error(res.data.message || "Failed to upload pricing ❌");
        setError(res.data.message || "Failed to upload pricing ❌");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Failed to upload pricing ❌";
      toast.error(errMsg);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Custom styles for react-select - Black & White
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      height: '36px',
      minHeight: '36px',
      borderColor: state.isFocused ? '#000000' : '#d1d5db',
      borderRadius: '0.5rem',
      fontSize: '0.75rem',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0,0,0,0.1)' : 'none',
      '&:hover': {
        borderColor: '#000000'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '36px',
      padding: '0 8px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      fontSize: '0.75rem'
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '0.75rem',
      backgroundColor: state.isSelected ? '#000000' : state.isFocused ? 'rgba(0,0,0,0.05)' : 'transparent',
      color: state.isSelected ? 'white' : '#1f2937',
      '&:active': {
        backgroundColor: '#000000'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '0.75rem'
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.75rem'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '0.75rem'
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        {/* Success & Error Messages */}
        {successMessage && (
          <div className="flex justify-end">
            <div className="border border-black/20 bg-black/5 flex justify-center w-full sm:w-auto text-black text-[10px] rounded-lg px-3 py-2 items-center gap-2">
              ✅ {successMessage}
            </div>
          </div>
        )}
        {error && (
          <div className="flex justify-end">
            <div className="border border-red-500/30 bg-red-50 flex justify-center items-center rounded-lg w-full sm:w-auto py-2 px-3">
              <p className="text-red-500 text-[10px]">{error}</p>
            </div>
          </div>
        )}

        {/* Template Download Section */}
        <div className="border bg-white border-black/10 rounded-lg p-3 sm:p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-blue-600 rounded-full">
                <FileText className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[10px] text-black">Need a template?</h3>
                <p className="text-[9px] text-black/60">Download sample CSV files</p>
              </div>
            </div>

            <div className="flex gap-1.5 w-full sm:w-auto">
              <a
                href="/sampleFile/b2brate.csv"
                download
                className="px-2.5 py-2 sm:py-1.5 bg-blue-600 text-white rounded-lg text-[9px] flex items-center justify-center gap-1 hover:bg-blue-700 transition-colors w-full sm:w-auto"
              >
                <FiDownload size={11} /> B2B Rate
              </a>

              {/* 
                href="/sampleFile/b2bratevas.csv"
                download
                className="px-2.5 py-1.5 bg-blue-600 text-white rounded-lg text-[9px] flex items-center gap-1 hover:bg-blue-700  transition-colors"
              >
                <FiDownload size={11} /> VAS Rate
              </a> */}
            </div>
          </div>
        </div>

        {/* Main Upload Section */}
        <div className="border bg-white border-black/10 rounded-lg overflow-hidden">
          {/* Dropdowns */}
          <div className="p-3 border-b border-black/10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Retailer */}
              <div>
                <label className="block text-[9px] font-medium text-black/70 mb-1">
                  Select Retailer <span className="text-red-500">*</span>
                </label>
                <Select
                  value={selectedRetailer}
                  onChange={setSelectedRetailer}
                  options={retailers}
                  isLoading={loading}
                  isDisabled={loading}
                  placeholder={loading ? "Loading retailers..." : "Search retailer..."}
                  styles={customSelectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable={true}
                  noOptionsMessage={() => "No retailers found"}
                  loadingMessage={() => "Loading..."}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              {/* Courier */}
              <div>
                <label className="block text-[9px] font-medium text-black/70 mb-1">
                  Select Courier Partner <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCourier}
                  onChange={(e) => setSelectedCourier(e.target.value)}
                  className="w-full px-2 py-1 text-[10px] border border-black/20 rounded-lg focus:outline-none focus:ring-1 focus:ring-black"
                  style={{ height: '36px' }}
                >
                  <option value="">-- Select Courier --</option>
                  {courierPartners.map((courier) => (
                    <option key={courier.id} value={courier.id}>
                      {courier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* CFT */}
              <div>
                <label className="block text-[9px] font-medium text-black/70 mb-1">
                  Select CFT <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCftType}
                  onChange={(e) => setSelectedCftType(e.target.value)}
                  disabled={loading}
                  className="w-full px-2 py-1 text-[10px] border border-black/20 rounded-lg focus:outline-none focus:ring-1 focus:ring-black disabled:bg-black/5 disabled:cursor-not-allowed"
                  style={{ height: '36px' }}
                >
                  <option value="">-- Select CFT --</option>
                  <option value="lite">Lite</option>
                  <option value="heavy">Heavy</option>
                  <option value="dense">Dense</option>
                </select>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="p-3 w-full">
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4 sm:items-end">
              <div className="w-full sm:w-[70%]">
                <label className="block text-[9px] font-medium text-black/70 mb-1">
                  Zone Pricing CSV <span className="text-red-500">*</span>
                </label>
                <label className="cursor-pointer border border-dashed border-black/30 rounded-lg px-3 py-2 sm:py-1.5 text-[9px] text-black/60 hover:border-black/60 transition-colors block w-full">
                  {zoneFile ? (
                    <span className="text-black font-medium">{zoneFile.name}</span>
                  ) : (
                    "Choose Zone Pricing CSV"
                  )}
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setZoneFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>

              <button
                onClick={handleUpload}
                disabled={loading}
                className="h-[36px] sm:h-[32px] px-4 bg-blue-600 w-full sm:w-[30%] text-white rounded-lg text-[10px] font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Uploading..." : "Upload Pricing"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingTemplateUpload;